import { useEffect, useRef, useState, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import Flex from "../../../../components/flex"
import HeaderWrapperMobile from "../header-wrapper-mobile"
import ImageBox from "@/components/image-box"

import DropdownDong from "../_parts/dropdown-dong"
import DropdownHo from "../_parts/dropdown-ho"

import { unitsStore } from "@/routes/apt/[apt_code]/units/units-store"
import { langStore } from "@/stores/lang-store"
import { aptSearchStore } from "@/routes/select/apt/apt-search-store"

import { formatMessage } from "@/utils/format-message"
import Hint from "../../../../components/hint"
import { headerBundle } from "./units-header-const"

const UnitsHeaderMobile = ({ position = "sticky" }) => {
  const navigate = useNavigate()
  const { lang } = langStore()

  const [searchParams, setSearchParams] = useSearchParams()
  const dong = searchParams.get("dong")
  const ho = searchParams.get("ho")
  const pickApt = aptSearchStore((s) => s.pickApt)

  const currentResource = searchParams.get("resource") || "water"

  const {
    draftDong,
    setDraftDong,
    draftHo,
    setDraftHo,

    originDongs,
    dongHo,

    unitStatusMap
  } = unitsStore()

  const dongOptions = useMemo(() => {
    return originDongs
      .map((d) => ({
        id: d,
        label: formatMessage(headerBundle.dong[lang], { dong: +d })
      }))
      .sort((a, b) => Number(a.id) - Number(b.id))
  }, [originDongs, lang])

  const hoOptions = useMemo(() => {
    if (!dongHo[draftDong]) return []

    return dongHo[draftDong].map((ho) => ({
      id: ho,
      label: formatMessage(headerBundle.ho[lang], { ho }),
      badges: unitStatusMap?.[draftDong]?.hos?.[ho]?.badges || []
    }))
  }, [dongHo, draftDong, unitStatusMap, lang])

  const dongChange = (dong) => {
    setDraftDong(dong)
    setDraftHo("")
  }

  const handleSearch = () => {
    if (!draftDong) return toast.error(headerBundle.alert.dongFirst[lang])
    if (!draftHo) return toast.error(headerBundle.alert.hoFirst[lang])

    const next = new URLSearchParams(searchParams)
    next.set("dong", draftDong)
    next.set("ho", draftHo)

    if (!searchParams.get("resource")) next.set("resource", "water")

    setSearchParams(next)
    setOpen(false)
  }

  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const [dropdownTop, setDropdownTop] = useState(0)

  useEffect(() => {
    if (open && rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect()
      setDropdownTop(rect.bottom)
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    const onDown = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) return
      setOpen(false)
    }

    document.addEventListener("mousedown", onDown)
    document.addEventListener("touchstart", onDown)

    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("touchstart", onDown)
    }
  }, [open])

  const [showSearchHint, setShowSearchHint] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSearchHint(false)
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <HeaderWrapperMobile position={position}>
      <Flex className="items-center w-full justify-between" gap={2}>
        <div
          className="flex items-center gap-3 cursor-pointer hover:opacity-70 transition-opacity"
          onClick={() => navigate("/select/apt")}
        >
          <ImageBox
            src="/images/toicos-logo-a.png"
            className="w-8 h-8 object-contain"
            alt="Toicos Logo"
          />

          <h1 className="font-bold text-lg text-slate-800 truncate max-w-[50vw]">
            {pickApt.apt_name}
          </h1>
        </div>

        <Flex className="items-center relative mr-3" gap={3}>
          <div ref={rootRef} className="relative">
            <Hint
              show={(!dong || !ho) && showSearchHint && !open}
              message={headerBundle.searchHint[lang]}
            />
            <button
              onClick={() => {
                setOpen((v) => !v)
                setShowSearchHint(false)
              }}
              className={`h-10 w-10 rounded-md flex items-center justify-center cursor-pointer transition-colors
                ${open ? "bg-blue-100 text-blue-600" : "bg-transparent text-gray-700"}`}
            >
              <svg
                viewBox="0 0 24 24"
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
            </button>

            {open && (
              <div
                className="fixed left-0 right-0 z-50 px-4"
                style={{ top: dropdownTop + 8 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-white rounded-xl border border-gray-200 shadow-2xl p-4 w-full">
                  <div className="grid grid-cols-2 gap-2 space-y-4">
                    <DropdownDong
                      options={dongOptions}
                      value={draftDong}
                      onChange={dongChange}
                      placeholder={headerBundle.select.dong[lang]}
                      unitStatusMap={unitStatusMap}
                      currentResource={currentResource}
                    />

                    <DropdownHo
                      options={hoOptions}
                      value={draftHo}
                      onChange={setDraftHo}
                      placeholder={headerBundle.select.ho[lang]}
                      disabled={!draftDong}
                      prevent={() => !draftDong && toast.error(headerBundle.alert.dongFirst[lang])}
                      currentResource={currentResource}
                    />
                  </div>
                  <button
                    onClick={handleSearch}
                    className="w-full py-2.5 rounded-lg bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-colors"
                  >
                    {headerBundle.search[lang]}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Flex>
      </Flex>
    </HeaderWrapperMobile>
  )
}

export default UnitsHeaderMobile
